import { type ReactNode, useRef, useState } from "react";

import {
  Avatar,
  Badge,
  Box,
  Checkbox,
  Column,
  DeleteIcon,
  DownloadIcon,
  FolderMoveIcon,
  LinkIcon,
  Menu,
  MenuIconButton,
  MenuItem,
  MenuList,
  MenuActionsButton,
  OverflowMenuIcon,
  Row,
  Skeleton,
  SkeletonBox,
  Text,
  TextWithTooltip,
  Tooltip,
} from "@hightouch/htui";
import { useInView } from "motion/react";

import { DeleteButton } from "src/components/delete-button";
import { ReviewAgentAvatar } from "src/components/comments/review-agent-avatar";
import { CardSelectionCheckbox } from "src/pages/ad-studio/components/card-selection-checkbox";
import { useAmpLibraryFileQuery } from "src/graphql";
import { attachmentFacts } from "src/pages/projects/attachment-facts";
import { isPendingId } from "src/pages/projects/board-cache";
import { useBoard } from "src/pages/projects/board-context";
import {
  useAttachmentActions,
  useIsAttachmentSelected,
  useIsSelecting,
} from "src/pages/projects/components/attachment-actions-context";
import { EmailCampaignMenuItem } from "src/pages/projects/components/attachment-email-campaign-item";
import { TaskLink } from "src/pages/projects/components/board-links";
import { RemoveAttachmentsDialog } from "src/pages/projects/components/remove-attachments-dialog";
import {
  CardFooter,
  CardFrame,
} from "src/pages/projects/components/card-shell";
import { ProjectIcon } from "src/pages/projects/components/project-icon";
import { RelativeTime } from "src/pages/projects/components/relative-time";
import { StatusIcon } from "src/pages/projects/components/status-icon";
import { useCreateCampaign } from "src/pages/projects/library/canvas/use-create-campaign";
import { IconTile } from "src/pages/context-hub/components/knowledge-list/name-cell";
import { FileThumbnail } from "src/pages/projects/library/file-card";
import { isExternalHref, KIND_ICONS } from "src/pages/projects/links";
import {
  type AttachmentActor,
  attachmentActor,
  attachmentRelink,
  attachmentSummary,
  isDownloadable,
  ON_PROJECT,
  type ProjectAttachment,
  uploadFileId,
} from "src/pages/projects/project-attachments";
import { useImageUpload } from "src/pages/projects/use-image-upload";
import { taskCategory } from "src/pages/projects/utils";
import { Link } from "src/router";

// A preview drawn at this size and scaled into a 40px tile, so it keeps the card's composition.
const MINI_PREVIEW_PX = 200;
const MINI_PREVIEW_SCALE = 0.2;

const SKELETON_ROW_KEYS = ["a", "b", "c", "d", "e"];
// List widths at which a row has room for its optional columns, beside the title it never gives up.
const ROW_FITS_ATTACHED_TO = "@container (min-width: 560px)";
const ROW_FITS_UPDATED = "@container (min-width: 400px)";

// The look of the task an attachment hangs on, shared by its link in a row and its label in a heading.
const TASK_CHIP_PROPS = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  minWidth: 0,
  maxWidth: "100%",
  px: 1,
  py: 0.5,
  bg: "base.lightBackground",
  borderWidth: "hairline",
  borderColor: "base.border",
  borderRadius: "md",
} as const;

type ProjectSummary = { title: string; icon: string | null };

type SourceTask = NonNullable<ProjectAttachment["link"]["source_task"]>;

type ItemProps = {
  attachment: ProjectAttachment;
  /** The page's project, named when an attachment hangs on it directly. */
  project: ProjectSummary;
};

/** Creative work as a card with its preview, on the Attachments tab. */
export const AttachmentCard = ({ attachment, project }: ItemProps) => {
  const { fileId } = attachment;
  const actions = useAttachmentActions();
  const isSelected = useIsAttachmentSelected(attachment.link.id);
  const isSelecting = useIsSelecting();
  return (
    <CardFrame isInteractive isSelected={isSelected}>
      <AttachmentCardPreview>
        {fileId && <LinkedFilePreview attachment={attachment} />}
      </AttachmentCardPreview>
      {actions && !isPendingId(attachment.link.id) && (
        <Box
          position="absolute"
          top={2}
          left={2}
          zIndex={2}
          // Shown on hover, and on every card once one is selected, so the rest can join it.
          opacity={isSelecting ? 1 : 0}
          _groupHover={{ opacity: 1 }}
          // Keyboard users need to see the control they tab to; there is no pseudo prop for this.
          sx={{ "&:has(:focus-visible)": { opacity: 1 } }}
        >
          <CardSelectionCheckbox
            isSelected={isSelected}
            label={`${isSelected ? "Deselect" : "Select"} ${attachment.described.title}`}
            onToggle={() => actions.toggle(attachment.link.id)}
          />
        </Box>
      )}
      <Box position="absolute" top={2} right={2} zIndex={2}>
        <AttachmentMenu attachment={attachment} />
      </Box>
      <CardFooter>
        <Column gap={0.5} minWidth={0}>
          <AttachmentTitle attachment={attachment} overlayRadius="3xl" />
          {/* Above the card's link, so the full line shows on hover once it truncates. */}
          <Box position="relative" zIndex={1} minWidth={0}>
            <TextWithTooltip size="sm" color="text.secondary">
              {secondaryLine(attachment)}
            </TextWithTooltip>
          </Box>
        </Column>
        <Row align="center" justify="space-between" gap={2} minHeight={6}>
          <AttachedTo attachment={attachment} project={project} />
          <AttachmentActorAvatar actor={attachmentActor(attachment.link)} />
        </Row>
      </CardFooter>
    </CardFrame>
  );
};

/** The attachment cards at their loaded size. */
export const AttachmentCardSkeleton = () => (
  <CardFrame>
    <AttachmentCardPreview />
    <CardFooter>
      <Column gap={0.5}>
        <SkeletonBox width="60%" height={6} />
        <SkeletonBox width="40%" height={5} />
      </Column>
      <SkeletonBox width={32} height={6} borderRadius="md" />
    </CardFooter>
  </CardFrame>
);

const AttachmentCardPreview = ({ children }: { children?: ReactNode }) => (
  <Box
    aria-hidden
    pointerEvents="none"
    height={36}
    overflow="hidden"
    borderTopRadius="3xl"
    bg="base.backgroundDisabled"
  >
    {children}
  </Box>
);

type RowProps = ItemProps & {
  /** Shows creative work's own preview in place of its kind's icon. */
  showPreview?: boolean;
  /** "task" is for a task's own page, where everything listed hangs on that task: no "attached to" column, and who attached it beside when. */
  variant?: "project" | "task";
};

/** An attachment as a row: what it is, where it's attached, and when it last changed. */
export const AttachmentRow = ({
  attachment,
  project,
  showPreview = false,
  variant = "project",
}: RowProps) => {
  const isTaskVariant = variant === "task";
  const facts = attachmentFacts(attachment.link);
  const actions = useAttachmentActions();
  const isSelected = useIsAttachmentSelected(attachment.link.id);
  return (
    <Row
      as="li"
      className="group"
      position="relative"
      align="center"
      gap={2}
      py={3}
      px={3}
      minWidth={0}
      bg={isSelected ? "primary.background" : undefined}
      borderBottomWidth="hairline"
      borderColor="base.divider"
      _last={{ borderBottomWidth: 0 }}
      _hover={
        attachment.described.href && !isSelected
          ? { bg: "base.lightBackground" }
          : {}
      }
    >
      {actions && !isPendingId(attachment.link.id) && (
        // Above the row's link, so ticking it doesn't open the attachment.
        <Box position="relative" zIndex={1} flexShrink={0}>
          <Checkbox
            isChecked={isSelected}
            aria-label={`${isSelected ? "Deselect" : "Select"} ${attachment.described.title}`}
            onChange={() => actions.toggle(attachment.link.id)}
          />
        </Box>
      )}
      <AttachmentThumbnail attachment={attachment} showPreview={showPreview} />
      {/* The title and where it's attached share the shrinking, so neither cell starves on a narrow row. */}
      <Column flex="1 1 var(--chakra-sizes-48)" minWidth={0}>
        <AttachmentTitle attachment={attachment} />
        {/* Wraps in full rather than truncating: the row's stretched link covers it, so a tooltip couldn't open. */}
        <Text size="sm" color="text.secondary">
          {secondaryLine(attachment)}
        </Text>
      </Column>
      {facts.badge && (
        <Badge
          size="sm"
          variant={facts.badge.variant}
          progress={facts.badge.progress}
        >
          {facts.badge.label}
        </Badge>
      )}
      {!isTaskVariant && (
        <Row
          display="none"
          // Container queries, not breakpoints: the list can be narrow on a wide screen, beside the project's chat sessions.
          sx={{ [ROW_FITS_ATTACHED_TO]: { display: "flex" } }}
          flex="0 1 var(--chakra-sizes-56)"
          minWidth={0}
        >
          <AttachedTo attachment={attachment} project={project} />
        </Row>
      )}
      <Row
        display={isTaskVariant ? "flex" : "none"}
        sx={
          isTaskVariant
            ? undefined
            : { [ROW_FITS_UPDATED]: { display: "flex" } }
        }
        width={isTaskVariant ? undefined : 36}
        flexShrink={0}
        align="center"
        justify="flex-end"
        gap={2}
        // Above the row's link, so the exact time shows on hover.
        position="relative"
        zIndex={1}
      >
        <AttachmentActorAvatar actor={attachmentActor(attachment.link)} />
        <RelativeTime date={attachment.touchedAt} label="Updated" />
      </Row>
      {isTaskVariant ? (
        <TaskAttachmentMenu attachment={attachment} />
      ) : (
        <AttachmentMenu attachment={attachment} />
      )}
    </Row>
  );
};

/** Rows of attachments in one bordered list. */
export const AttachmentRowList = ({
  children,
  borderRadius = "lg",
}: {
  children: ReactNode;
  borderRadius?: "lg" | "2xl";
}) => (
  <Column
    as="ul"
    listStyleType="none"
    m={0}
    p={0}
    // The rows hide their optional columns by this list's width.
    sx={{ containerType: "inline-size" }}
    bg="white"
    borderWidth="hairline"
    borderColor="base.border"
    borderRadius={borderRadius}
    overflow="hidden"
  >
    {children}
  </Column>
);

/** Attachment rows at their loaded size. */
export const AttachmentRowsSkeleton = ({ rows }: { rows: number }) => (
  <Skeleton isLoading>
    <AttachmentRowList>
      {SKELETON_ROW_KEYS.slice(0, rows).map((key) => (
        <Row
          key={key}
          as="li"
          align="center"
          gap={2}
          p={3}
          borderBottomWidth="hairline"
          borderColor="base.divider"
          _last={{ borderBottomWidth: 0 }}
        >
          <SkeletonBox boxSize={10} borderRadius="lg" />
          <Column flex={1} gap={1}>
            <SkeletonBox width="40%" height={4} borderRadius="sm" />
            <SkeletonBox width="25%" height={3} borderRadius="sm" />
          </Column>
        </Row>
      ))}
    </AttachmentRowList>
  </Skeleton>
);

/** Where an attachment hangs: its task, with the task's status, or the project itself. */
const AttachedTo = ({ attachment, project }: ItemProps) => {
  const task = attachment.link.source_task;
  if (!task) return <ProjectTarget project={project} />;
  return (
    <TaskLink
      taskId={task.id}
      // Above the attachment's link, so the task opens on its own.
      position="relative"
      zIndex={1}
      {...TASK_CHIP_PROPS}
      _hover={{ bg: "base.background" }}
      _focusVisible={{ boxShadow: "outline", outline: "none" }}
    >
      <TaskChipContent task={task} />
    </TaskLink>
  );
};

/** Where a group of attachments hangs, as `AttachedTo` shows it but without a link, for a heading that is itself a control. */
export const AttachmentTarget = ({
  task,
  project,
}: {
  /** Null for the project itself. */
  task: SourceTask | null;
  project: ProjectSummary;
}) =>
  task ? (
    <Box {...TASK_CHIP_PROPS}>
      <TaskChipContent task={task} />
    </Box>
  ) : (
    <ProjectTarget project={project} />
  );

const TaskChipContent = ({ task }: { task: SourceTask }) => {
  const category = taskCategory(task);
  return (
    <>
      <StatusIcon
        status={task.status}
        category={task.status ? undefined : category}
      />
      <Box minWidth={0}>
        <TextWithTooltip size="sm" color="text.secondary">
          {task.title}
        </TextWithTooltip>
      </Box>
    </>
  );
};

const ProjectTarget = ({ project }: { project: ProjectSummary }) => (
  <Row align="center" gap={1} minWidth={0}>
    <ProjectIcon icon={project.icon} size={14} />
    <TextWithTooltip size="sm" color="text.secondary">
      {project.title}
    </TextWithTooltip>
  </Row>
);

/** The attachment's name, linking to it across the whole card or row when it has a page. */
const AttachmentTitle = ({
  attachment,
  overlayRadius,
}: {
  attachment: ProjectAttachment;
  overlayRadius?: "3xl";
}) => {
  const { title, href } = attachment.described;
  const text = (
    <Text isTruncated display="block">
      {title}
    </Text>
  );
  if (!href) {
    return (
      <Box minWidth={0} title={title}>
        {text}
      </Box>
    );
  }
  return (
    <Link
      href={href}
      // A full URL opens in a new tab.
      isExternal={isExternalHref(href)}
      minWidth={0}
      title={title}
      _focusVisible={{ outline: "none", _after: { boxShadow: "outline" } }}
      _after={{
        content: '""',
        position: "absolute",
        inset: 0,
        borderRadius: overlayRadius,
      }}
    >
      {text}
    </Link>
  );
};

/** Who attached it: their initials, or the agent avatar when an agent did. */
const AttachmentActorAvatar = ({ actor }: { actor: AttachmentActor }) =>
  actor.kind === "user" ? (
    <Avatar name={actor.name} size="md" />
  ) : (
    <Tooltip message="Attached by an agent">
      <Box display="flex">
        <ReviewAgentAvatar name="Agent" size="md" />
      </Box>
    </Tooltip>
  );

/** The attachment's actions, on hover or keyboard focus. Left out while it's still being added. */
const AttachmentMenu = ({ attachment }: { attachment: ProjectAttachment }) => {
  const { projects } = useBoard();
  const actions = useAttachmentActions();
  const [isConfirmingRemove, setIsConfirmingRemove] = useState(false);
  const campaign = useCreateCampaign();
  if (isPendingId(attachment.link.id)) return null;
  return (
    <Box
      position="relative"
      zIndex={1}
      flexShrink={0}
      opacity={0}
      _groupHover={{ opacity: 1 }}
      // Keyboard users need to see the button they tab to; there is no pseudo prop for this.
      sx={{
        "&:has(:focus-visible)": { opacity: 1 },
        "&:has([aria-expanded=true])": { opacity: 1 },
      }}
    >
      <Menu strategy="fixed" computePositionOnMount={false}>
        <MenuIconButton
          icon={OverflowMenuIcon}
          size="sm"
          aria-label={`Show actions for ${attachment.described.title}`}
        />
        <MenuList>
          <MenuItem
            icon={DeleteIcon}
            variant="danger"
            onClick={() => setIsConfirmingRemove(true)}
          >
            Remove attachment
          </MenuItem>
          {actions && isDownloadable(attachment) && (
            <MenuItem
              icon={DownloadIcon}
              onClick={() => actions.download([attachment])}
            >
              Download attachment
            </MenuItem>
          )}
          {actions &&
            attachmentRelink(attachment.link) &&
            (attachment.attachedTo === ON_PROJECT ? (
              <MenuItem
                icon={LinkIcon}
                onClick={() => actions.attachToTask([attachment])}
              >
                Attach to task
              </MenuItem>
            ) : (
              <MenuItem
                icon={FolderMoveIcon}
                onClick={() => actions.moveToProject([attachment])}
              >
                Move to project
              </MenuItem>
            ))}
          {attachment.link.amp_file?.content_email && (
            <EmailCampaignMenuItem
              attachment={attachment}
              onCreate={campaign.requestCreate}
            />
          )}
        </MenuList>
      </Menu>
      {campaign.dialog}
      {isConfirmingRemove && (
        <RemoveAttachmentsDialog
          count={1}
          isOpen
          onClose={() => setIsConfirmingRemove(false)}
          onConfirm={() =>
            actions
              ? actions.remove([attachment])
              : projects.unlink(attachment.link.id)
          }
        />
      )}
    </Box>
  );
};

/** What an attachment looks like in a list: its own preview where it has one, otherwise its kind's icon tile. */
export const AttachmentThumbnail = ({
  attachment,
  showPreview = false,
}: {
  attachment: ProjectAttachment;
  /** Shows creative work's own preview in place of its kind's icon. */
  showPreview?: boolean;
}) => {
  const uploadId = uploadFileId(attachment);
  if (showPreview && attachment.fileId) {
    return (
      <MiniPreview>
        <LinkedFilePreview attachment={attachment} />
      </MiniPreview>
    );
  }
  if (uploadId) return <UploadTile attachment={attachment} fileId={uploadId} />;
  return <KindTile attachment={attachment} />;
};

/** Takes a task's attachment off from a menu that is always in reach. Left out while it's still being added. */
const TaskAttachmentMenu = ({
  attachment,
}: {
  attachment: ProjectAttachment;
}) => {
  const { projects } = useBoard();
  if (isPendingId(attachment.link.id)) return null;
  return (
    // Above the row's link, and kept mounted so its confirmation survives the menu closing.
    <Box position="relative" zIndex={1} flexShrink={0}>
      <Menu isLazy={false}>
        <MenuActionsButton size="sm" />
        <MenuList>
          <DeleteButton
            variant="menu"
            action="remove"
            label="attachment"
            onDelete={async () => projects.unlink(attachment.link.id)}
          />
        </MenuList>
      </Menu>
    </Box>
  );
};

/** The attachment's kind, as its icon on a tile. */
const KindTile = ({ attachment }: { attachment: ProjectAttachment }) => (
  <Box aria-hidden flexShrink={0}>
    <IconTile icon={KIND_ICONS[attachment.described.kind].icon} />
  </Box>
);

/** An uploaded image as its own thumbnail on the tile. Any other upload keeps its kind's icon. */
const UploadTile = ({
  attachment,
  fileId,
}: {
  attachment: ProjectAttachment;
  fileId: string;
}) => {
  const { ref, image } = useImageUpload(fileId);
  return (
    <Box ref={ref} flexShrink={0}>
      {image ? (
        <MiniPreview>
          <FileThumbnail file={image} />
        </MiniPreview>
      ) : (
        <KindTile attachment={attachment} />
      )}
    </Box>
  );
};

/** A file's preview, scaled down to a row's tile. */
const MiniPreview = ({ children }: { children: ReactNode }) => (
  <Box
    aria-hidden
    pointerEvents="none"
    boxSize={10}
    flexShrink={0}
    overflow="hidden"
    bg="base.backgroundDisabled"
    borderRadius="lg"
    boxShadow="xs"
  >
    <Box
      width={`${MINI_PREVIEW_PX}px`}
      height={`${MINI_PREVIEW_PX}px`}
      transform={`scale(${MINI_PREVIEW_SCALE})`}
      transformOrigin="top left"
    >
      {children}
    </Box>
  </Box>
);

/** The attached Library file's preview, loaded once it nears the screen. Its kind's icon when the file can't be read. */
export const LinkedFilePreview = ({
  attachment,
}: {
  attachment: ProjectAttachment;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const isNearViewport = useInView(ref, { margin: "200px" });
  const fileQuery = useAmpLibraryFileQuery(
    { id: attachment.fileId ?? "" },
    { enabled: isNearViewport && attachment.fileId !== null },
  );
  const file = fileQuery.data?.ampFile;
  const { icon: Icon } = KIND_ICONS[attachment.described.kind];
  return (
    <Box ref={ref} height="100%">
      {file ? (
        <FileThumbnail file={file} />
      ) : fileQuery.error ? (
        <Row height="100%" align="center" justify="center">
          <Box as={Icon} boxSize={8} color="text.tertiary" />
        </Row>
      ) : null}
    </Box>
  );
};

function secondaryLine({ described, link }: ProjectAttachment): string {
  return attachmentSummary(described, attachmentFacts(link).facts).join(" · ");
}
